import React, { useState, useEffect, useMemo, Suspense, useCallback, Fragment, ChangeEvent } from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction, set } from 'mobx';
import { InvoiceFieldsNew } from '../InvoiceFieldsNew/InvoiceFieldsNew';
import EventLabel from '../../components/EventLabel/EventLabel';
import { CountrySelect } from '../CountrySelect/CountrySelect';
import _ from 'lodash';
import { useRef } from 'react';
import { OrderRow, Country, PaymentMethod, PaymentType } from './types';
import CustomInput from '../../components/custominput/CustomInput';
import { getCalculatedOrderRow } from '../OrderSummary/ConfirmationOrderRow';
import { defaultInvoiceFields } from '../../constants/invoiceFields.constants';
import { basedAxios } from '../../store/Store';

import PaymentMethodButton from './PaymentMethodButton';
import PaymentTypeButton from './PaymentTypeButton';

interface PaymentOptionsProps {
  settings: any;
  addOrUpdate: any;
  orderLeaderIndex: any;
  orders: any;
  updateOrder: any;
  current: any;
  contentModuleId: any;
  validations: any;
  checkHeights: any;
  environment: any;
  eventId: any;
  mainEvent: any;
  setValidatePaymentFields: any;
  validatePaymentFields: any;
  breakouts: any;
  countries: any;
  modules: any;
  ordersLoaded: any;
  activePersonNo: number;
}

function PaymentOptions(props: PaymentOptionsProps) {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [fixedCountry, setFixedCountry] = useState<boolean>(false);
  const [regionGroup, setRegionGroup] = useState<string>('global');

  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>(props?.settings?.paymentTypes ?? []);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(props?.settings?.paymentMethods ?? []);

  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

  const [validateVat, setValidateVat] = useState<boolean | null>(false);
  const [validVatNo, setValidVatNo] = useState<boolean | null>(null);
  const [vatNo, setVatNo] = useState<string>('');
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const invoiceAnchor = useRef<HTMLDivElement>(null);

  const {
    settings,
    orders,
    updateOrder,
    current,
    validations,
    ordersLoaded,
    environment,
    breakouts,
    modules,
    activePersonNo,
    mainEvent,
    setValidatePaymentFields,
    validatePaymentFields,
  } = props;

  const { collectInvoiceInfoOnCompanyCardPayment = false, ignoreVatOutsideEU = false } = settings;

  const orderLeaderIndex = useMemo(() => {
    const index = orders.findIndex((order: any) => order.leader);
    return index === -1 ? 0 : index;
  }, [orders]);

  const handleCountryChange = useCallback(
    (countryId: number, country: Country, reset: boolean = false) => {
      // if (countryId === selectedCountry?.countryId || !ordersLoaded) {
      //   return;
      // }

      setSelectedCountry(country);
      setSelectedPaymentType(null);
      setSelectedPaymentMethod(null);
      setPaymentMethods([]);
      setValidatePaymentFields(true);

      runInAction(() => {
        current.payMethod = null;
      });

      const validation = validations.find(({ label }: any) => label === 'PaymentOption');

      if (validation && validation?.valid) {
        runInAction(() => {
          validation.valid = false;
        });
      }

      if (reset) {
        runInAction(() => {
          orders[orderLeaderIndex].invoice = {};
          updateOrder(orders[orderLeaderIndex]);
        });
      }

      const regionPrefix = settings.paymentGrid?.[country?.name?.toLowerCase() as string] ? country?.name?.toLowerCase() : 'global';

      let newPaymentTypes = [PaymentType.Person, PaymentType.Comgov];

      if (
        !settings.paymentGrid?.[`${regionPrefix} dibs person`] &&
        !settings.paymentGrid?.[`${regionPrefix} stripe person`] &&
        !settings.paymentGrid?.[`${regionPrefix} invoice person`]
      ) {
        newPaymentTypes = newPaymentTypes?.filter((pt: string) => pt !== PaymentType.Person);
      }

      if (
        !settings.paymentGrid?.[`${regionPrefix} dibs comgov`] &&
        !settings.paymentGrid?.[`${regionPrefix} stripe comgov`] &&
        !settings.paymentGrid?.[`${regionPrefix} invoice comgov`]
      ) {
        newPaymentTypes = newPaymentTypes?.filter((pt: string) => pt !== PaymentType.Comgov);
      }

      if (newPaymentTypes?.length === 1) {
        handlePaymentTypeChange(newPaymentTypes[0] as PaymentType, country);
      }

      setPaymentTypes(newPaymentTypes);
      setRegionGroup(regionPrefix);
    },
    [validations, orders, orderLeaderIndex, orders, selectedCountry, updateOrder, setValidatePaymentFields, settings, ordersLoaded]
  );

  const hasReverseVatRows = useCallback(() => {
    const breakoutIdsWithReverseVat = [...breakouts, mainEvent]?.filter(breakout => breakout.allowReverseCharge)?.map(breakout => breakout.eventId);
    const orderRowWithReverseVat = orders[orderLeaderIndex]?.orderRow?.some((row: any) => breakoutIdsWithReverseVat.includes(row.eventId));

    return !!orderRowWithReverseVat;
  }, [breakouts, orders, orderLeaderIndex]);

  const handlePaymentMethodChange = useCallback(
    (newPaymentMethod: string) => {
      if (!ordersLoaded) return;
      if (selectedCountry?.europeCountry) {
        const orderCopy = _.cloneDeep(orders[orderLeaderIndex]);
        let newOrderExtra = orderCopy?.orderExtra ? _.cloneDeep(orderCopy?.orderExtra) : [];

        newOrderExtra = [...newOrderExtra.filter((extra: any) => extra.name !== 'vatExempt' && extra.name !== 'vatNo' && extra.name !== 'sweVat')];

        if (!orderCopy?.['orderExtra']) {
          orderCopy['orderExtra'] = [];
        }

        orderCopy['orderExtra'] = newOrderExtra;

        runInAction(() => {
          updateOrder(orderCopy, orderLeaderIndex);
        });

        setValidVatNo(null);
        setVatNo('');

        const notEqualToEventCountry =
          environment?.eventCountry &&
          selectedCountry?.countryCode !== environment?.eventCountry?.code &&
          selectedCountry?.europeCountry &&
          environment?.eventCountry?.europeCountry;

        const validateVat = hasReverseVatRows();

        setValidateVat(!!(validateVat && notEqualToEventCountry && newPaymentMethod !== PaymentMethod.Invoice && selectedPaymentType === PaymentType.Comgov));

        if (
          orders?.[activePersonNo]?.company?.vatNo &&
          !!(validateVat && notEqualToEventCountry && newPaymentMethod !== PaymentMethod.Invoice && selectedPaymentType === PaymentType.Comgov)
        ) {
          setVatNo(orders?.[activePersonNo]?.invoice?.vatNo);
        }
      }

      if (newPaymentMethod === PaymentMethod.Invoice) {
        const validationInvoiceFields = defaultInvoiceFields.map((field: any) => `${field}ValidationMessage`);
        const includedValidations = validations.filter(({ label }: any) => label?.includes('INVOICE') || validationInvoiceFields.includes(label));

        if (includedValidations.length) {
          runInAction(() => {
            includedValidations?.forEach((validation: any) => {
              validation.hidden = false;
            });
          });
        }
      } else if (newPaymentMethod !== PaymentMethod.Invoice && selectedPaymentType === PaymentType.Comgov && collectInvoiceInfoOnCompanyCardPayment) {
        const validationInvoiceFields = defaultInvoiceFields.map((field: any) => `${field}ValidationMessage`);
        const includedValidations = validations.filter(({ label }: any) => label?.includes('INVOICE') || validationInvoiceFields.includes(label));

        if (includedValidations.length) {
          runInAction(() => {
            includedValidations?.forEach((validation: any) => {
              if (!validation.id?.includes('INVOICE_Name')) {
                validation.hidden = true;
                validation.valid = true;
              } else {
                validation.hidden = false;
                validation.valid = false;
                validation.forced = true;
              }
            });
          });
        }
      } else if (newPaymentMethod === PaymentMethod.Stripe || newPaymentMethod === PaymentMethod.Dibs) {
        setValidatePaymentFields(false);
        const validation = validations.find(({ label }: any) => label === 'PaymentOption');
        if (validation) {
          runInAction(() => {
            validation.valid = true;
          });
        }
      }

      setSelectedPaymentMethod(newPaymentMethod as PaymentMethod);
      runInAction(() => {
        current.payMethod = newPaymentMethod;
      });
    },
    [
      current,
      hasReverseVatRows,
      selectedPaymentType,
      orders,
      orderLeaderIndex,
      selectedCountry,
      collectInvoiceInfoOnCompanyCardPayment,
      updateOrder,
      setValidateVat,
      setValidatePaymentFields,
      validations,
      setValidVatNo,
      setVatNo,
      ordersLoaded,
    ]
  );

  const handlePaymentTypeChange = useCallback(
    (newPaymentType: string, country: Country = null) => {
      if (!ordersLoaded) return;
      setSelectedPaymentType(newPaymentType as PaymentType);

      const currentCountry = country ?? selectedCountry;

      const regionPrefix = settings.paymentGrid?.[currentCountry?.name?.toLowerCase() as string] ? currentCountry?.name?.toLowerCase() : 'global';
      const allPaymentMethods = settings?.paymentMethods ?? [];

      let newPaymentMethods = allPaymentMethods?.reduce((acc: PaymentMethod[], method: PaymentMethod) => {
        const hasMethod = settings.paymentGrid?.[`${regionPrefix} ${method} ${newPaymentType}`];
        return hasMethod ? [...acc, method] : acc;
      }, []);

      // If it is a change link and the person has an invoice, remove the invoice option

      setPaymentMethods(newPaymentMethods);

      if (orders?.[orderLeaderIndex]?.invoice?.countryCode && !orders?.[orderLeaderIndex]?.badgeNo) {
        runInAction(() => {
          const orderCopy = _.cloneDeep(orders[orderLeaderIndex]);
          orderCopy.invoice = {};
          updateOrder(orderCopy, orderLeaderIndex);
        });
      }

      if (newPaymentType !== PaymentType.Comgov && !orders?.[orderLeaderIndex]?.badgeNo) {
        const orderCopy = _.cloneDeep(orders[orderLeaderIndex]);
        let newOrderExtra = orderCopy?.orderExtra ? _.cloneDeep(orderCopy?.orderExtra) : [];

        newOrderExtra = [...newOrderExtra.filter((extra: any) => extra.name !== 'vatExempt' && extra.name !== 'vatNo')];
        orderCopy['orderExtra'] = newOrderExtra;

        runInAction(() => {
          updateOrder(orderCopy, orderLeaderIndex);
        });
      }

      if (ignoreVatOutsideEU) {
        const breakoutIdsWithReverseVat = [...breakouts, mainEvent]?.filter(breakout => breakout.allowReverseCharge)?.map(breakout => breakout.eventId);
        const orderCopy = _.cloneDeep(orders[orderLeaderIndex]);
        const orderRowCopy = _.cloneDeep(orderCopy?.orderRow);

        const newCountry = country ?? selectedCountry;

        if (!newCountry?.europeCountry && !newCountry?.nordicCountry && newPaymentType === PaymentType.Comgov) {
          orderRowCopy?.map((row: any) => {
            if (breakoutIdsWithReverseVat.includes(row.eventId) && row.orderRowId < 1) {
              row.vatPercent = 0;
            }
            return row;
          });
        } else if ((newPaymentType !== PaymentType.Comgov || newCountry?.europeCountry) && !orderCopy.invoice?.vatNo) {
          orderRowCopy?.map((row: any) => {
            if (breakoutIdsWithReverseVat.includes(row.eventId) && row.orderRowId < 1) {
              row.vatPercent = [...breakouts, mainEvent].find(breakout => breakout.eventId === row.eventId)?.vatPercent;
            }
            return row;
          });
        }

        orderCopy.orderRow = orderRowCopy;
        updateOrder(orderCopy, orderLeaderIndex);
      }

      setSelectedPaymentMethod(null);

      runInAction(() => {
        current.payMethod = null;
      });

      const validationInvoiceFields = defaultInvoiceFields.map((field: any) => `${field}ValidationMessage`);
      const includedValidations = validations.filter(({ label }: any) => label?.includes('INVOICE') || validationInvoiceFields.includes(label));

      if (includedValidations.length) {
        runInAction(() => {
          includedValidations?.forEach((validation: any) => {
            validation.hidden = true;
          });
        });
      }

      if (newPaymentMethods?.length === 1) {
        handlePaymentMethodChange(newPaymentMethods[0] as PaymentMethod);
      }

      setValidateVat(false);
      setValidVatNo(null);
      setVatNo('');
    },
    [settings?.paymentGrid, selectedCountry, orders, updateOrder, orderLeaderIndex, ignoreVatOutsideEU, ordersLoaded]
  );

  const handleVatNoChange = useCallback(
    (vatNo: string) => {
      if (orders.length === 0) return;

      setValidVatNo(true);

      const orderCopy = _.cloneDeep(orders[orderLeaderIndex]);
      let newOrderExtra = orderCopy?.orderExtra ? _.cloneDeep(orderCopy?.orderExtra) : [];

      if (validVatNo && vatNo) {
        newOrderExtra = [
          ...newOrderExtra.filter((extra: any) => extra.name !== 'vatExempt' && extra.name !== 'vatNo' && extra.name !== 'sweVat'),
          { name: 'vatExempt', value: true, value2: '' },
          { name: 'vatNo', value: vatNo, value2: '' },
        ];
      } else {
        newOrderExtra = [
          ...newOrderExtra.filter((extra: any) => extra.name !== 'vatExempt' && extra.name !== 'vatNo' && extra.name !== 'sweVat'),
          { name: 'vatExempt', value: true, value2: '' },
          { name: 'sweVat', value: true, value2: '' },
        ];
      }
      orderCopy['orderExtra'] = newOrderExtra;
      runInAction(() => {
        updateOrder(orderCopy, orderLeaderIndex);
      });
    },
    [orders, orderLeaderIndex, validVatNo, updateOrder]
  );

  const handleValidateVatNo = useCallback(async () => {
    setIsValidating(true);
    try {
      await basedAxios.get(`./invoice/validatevat?vatNo=${vatNo}`);

      const breakoutIdsWithReverseVat = [...breakouts, mainEvent]?.filter(breakout => breakout.allowReverseCharge)?.map(breakout => breakout.eventId);
      const orderRowWithReverseVat = orders[orderLeaderIndex]?.orderRow?.map((row: any) => ({
        ...row,
        vatPercent: breakoutIdsWithReverseVat.includes(row.eventId) ? 0 : row.vatPercent,
      }));
      const orderCopy = _.cloneDeep(orders[orderLeaderIndex]);
      orderCopy.orderRow = orderRowWithReverseVat;

      if (!orderCopy?.invoice) orderCopy.invoice = {};

      orderCopy.invoice.vatNo = vatNo;

      updateOrder(orderCopy, orderLeaderIndex);

      setValidVatNo(true);
      handleVatNoChange(vatNo);
    } catch (error) {
      setValidVatNo(false);
      console.error(error);
    } finally {
      setIsValidating(false);
    }
  }, [vatNo, props?.countries, selectedCountry, handleVatNoChange, orders, breakouts, orderLeaderIndex, updateOrder]);

  useEffect(() => {
    if (((!selectedCountry && props?.environment?.defaultCountry) || (!selectedCountry && settings?.paymentGrid?.only?.length === 1)) && ordersLoaded) {
      let country = props?.countries?.find((country: any) => country.countryId === props?.environment?.defaultCountry);
      if (settings?.paymentGrid?.only?.length && settings?.paymentGrid?.only?.[0]?.countries?.[0]?.value) {
        country = props?.countries?.find((country: any) => country.countryId == settings?.paymentGrid?.only?.[0]?.countries?.[0]?.value);

        if (country) {
          setFixedCountry(true);
        }
      }

      if (!country) return;

      const formattedCountry = {
        countryId: country.countryId,
        value: country.countryId,
        label: environment.lang === 'swe' || environment.lang === undefined ? country.nameSwe : country.nameInt,
        europeCountry: country.europeCountry === null ? false : country.europeCountry,
        nordicCountry: country.nordicCountry === null ? false : country.nordicCountry,
        swedishCountry: country.nameInt === 'Sweden' ? true : false,
        countryCode: country.code,
        name: country.nameInt,
      };

      handleCountryChange(formattedCountry?.value, formattedCountry);
    }
  }, [handleCountryChange, props?.environment?.defaultCountry, props?.countries, selectedCountry, ordersLoaded]);

  const orderValue = useMemo(() => {
    const filteredOrders = orders.filter(order => order?.orderRow);
    const unpaidOrderAmount = orders?.[orderLeaderIndex]?.unpaidOrderAmount ?? 0;

    const calculatedOrderRows = filteredOrders.flatMap(({ orderId, orderRow = [] }) =>
      orderRow.map(orderRowItem => getCalculatedOrderRow(orderRowItem, breakouts, orders, orderId, mainEvent, modules))
    );

    const validProcessedOrderRows = calculatedOrderRows.filter((item: OrderRow) => item?.orderRowId < 1 && item?.amount > 0);

    const total = validProcessedOrderRows.reduce((acc: number, item: OrderRow) => acc + (item?.amount - item?.discountAmount || 0), 0);

    return total + unpaidOrderAmount;
  }, [orders, setValidatePaymentFields, breakouts, mainEvent, modules, orderLeaderIndex]);

  const hasInvoice = useMemo(() => {
    const invoice = orders?.[orderLeaderIndex]?.invoice;
    return invoice?.companyId && invoice?.name || invoice?.orgNo;
  }, [orders, orderLeaderIndex]);

  useEffect(() => {
    // If change link and person has an invoice, set selected country and preview the invoice fields
    // If a value is payable show all payment options, not just the invoice option

    const includedValidations = validations.filter((val: any) => val.label === 'PaymentOption' || val?.id?.toString().includes('INVOICE'));

    if (includedValidations.length) {
      runInAction(() => {
        includedValidations?.forEach((validation: any) => {
          validation.valid = orderValue > 0 ? false : true;
        });
      });
    }

    if (hasInvoice) {
      const invoice = orders?.[orderLeaderIndex]?.invoice;
      const country = props?.countries?.find((country: any) => country.code === invoice?.countryCode || country.countryId === invoice?.countryId);
      const isCompany = invoice?.companyName ?? invoice?.orgNo;
      const newPaymentType = isCompany ? PaymentType.Comgov : PaymentType.Person;

      const regionPrefix = settings.paymentGrid?.[country?.nameInt?.toLowerCase() as string] ? country?.nameInt?.toLowerCase() : 'global';

      if (country) {
        
        const formattedCountry = {
          countryId: country.countryId,
          value: country.countryId,
          label: environment.lang === 'swe' || environment.lang === undefined ? country.nameSwe : country.nameInt,
          europeCountry: country.europeCountry === null ? false : country.europeCountry,
          nordicCountry: country.nordicCountry === null ? false : country.nordicCountry,
          swedishCountry: country.nameInt === 'Sweden' ? true : false,
          countryCode: country.code,
          name: country.nameInt,
        };

        setSelectedCountry(formattedCountry);

        if (settings.paymentGrid[`${regionPrefix} invoice ${newPaymentType}`]) {
          handlePaymentTypeChange(newPaymentType, formattedCountry);
          handlePaymentMethodChange(PaymentMethod.Invoice);

          if (orders?.[orderLeaderIndex]?.badgeNo) {
            const validationInvoiceFields = defaultInvoiceFields.map((field: any) => `${field}ValidationMessage`);
            const includedValidations = validations.filter(({ label }: any) => label?.includes('INVOICE') || validationInvoiceFields.includes(label));

            if (includedValidations.length) {
              runInAction(() => {
                includedValidations?.forEach((validation: any) => {
                  validation.valid = true;
                });
              });
            }
          }
        }
      }
    }
  }, [
    orders?.[orderLeaderIndex]?.invoice?.countryCode,
    ordersLoaded,
    orderLeaderIndex,
    orderValue,
    props?.countries,
    settings?.paymentMethods,
    settings?.paymentTypes,
    setValidatePaymentFields,
    hasInvoice,
  ]);

  useEffect(() => {
    if (orderValue <= 0 && orders?.[orderLeaderIndex]?.badgeNo && !hasInvoice) {
      setValidatePaymentFields(false);
    } else if (orderValue > 0) {
      setValidatePaymentFields(true);
    }
  }, [orderValue, setValidatePaymentFields, orders, orderLeaderIndex, hasInvoice]);

  // If module is hidden or the order have no value, return null
  if (
    ((settings.hidden || orderValue <= 0) && orderValue <= 0 && !orders?.[orderLeaderIndex]?.badgeNo && !hasInvoice) ||
    (orderValue <= 0 && orders?.[orderLeaderIndex]?.badgeNo && !hasInvoice)
  ) {
    return null;
  }

  // If no country is selected and module has no paymentOptions or paymentMethods, show a label
  if (!selectedCountry && paymentTypes.length === 0 && paymentMethods.length === 0) {
    return (
      <div>
        {paymentTypes.length === 0 && (
          <label className="block">
            <EventLabel name={'NoPaymentTypesConfigured'} groupName={'CMS.Payment'} />
          </label>
        )}
        {paymentMethods.length === 0 && (
          <label className="block">
            <EventLabel name={'NoPaymentMethodsConfigured'} groupName={'CMS.Payment'} />
          </label>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="cms-field invoice form-group">
        {!fixedCountry && (
          <>
            <div className="title-holder">
              <label>
                <EventLabel name={'SelectCountry'} groupName={'CMS.Payment'} />
              </label>
            </div>
            <CountrySelect
              value={selectedCountry?.value ?? props?.environment?.defaultCountry}
              eventId={environment.eventId}
              lang={environment.lang}
              countryGroup={environment.countryGroup}
              onChange={handleCountryChange}
              disabled={orderValue === 0 && orders?.[orderLeaderIndex]?.badgeNo && orders?.[orderLeaderIndex]?.invoice} // Disable select if changelink with no value but has an invoice
              selectGrid={settings.paymentGrid}
            />
          </>
        )}
      </div>
      <div>
        {selectedCountry && paymentTypes.length > 1 && (
          <div className="mb-4">
            <div className="title-holder mb-2">
              <label>
                <EventLabel name={'SelectPaymentType'} groupName={'CMS.Payment'} />
              </label>
            </div>
            <div className="flex flex-col gap-x-2 rounded-md divide-y border border-neutral-200 divide-neutral-200 w-[300px]">
              {paymentTypes
                ?.slice()
                ?.sort()
                ?.map(option => (
                  <Fragment key={option}>
                    <PaymentTypeButton option={option} selectedOption={selectedPaymentType} onSelectOption={handlePaymentTypeChange} />
                  </Fragment>
                ))}
            </div>
          </div>
        )}
        {selectedPaymentType && paymentMethods.length > 1 && (
          <div className="mb-4">
            <div className="title-holder mb-2">
              <label>
                <EventLabel name={'SelectPaymentMethod'} groupName={'CMS.Payment'} />
              </label>
            </div>
            <div className="flex flex-col gap-x-2 rounded-md divide-y border border-neutral-200 divide-neutral-200 w-[300px]">
              {paymentMethods
                ?.slice()
                ?.filter(x => !([PaymentMethod.Dibs, PaymentMethod.Stripe].every(item => paymentMethods.includes(item)) && x === PaymentMethod.Dibs))
                ?.map(paymentMethod => (
                  <Fragment key={paymentMethod}>
                    <PaymentMethodButton option={paymentMethod} selectedOption={selectedPaymentMethod} onSelectOption={handlePaymentMethodChange} />
                  </Fragment>
                ))}
            </div>
          </div>
        )}
      </div>

      {validateVat && selectedPaymentMethod && selectedPaymentMethod !== PaymentMethod.Invoice && !collectInvoiceInfoOnCompanyCardPayment && (
        <CustomInput
          required
          prefixClassName="cms-field invoice"
          value={vatNo}
          readOnly={validVatNo}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setVatNo(event?.target?.value)}
          label={'vatNo'}
          staticLabel={true}
          type={'vatNo'}
          invoice={true}
          lang={environment.lang}
          eventId={environment.eventId}
          noLabel={false}
          noInputField={false}
          appendButton={
            validVatNo ? (
              <i className="ri-checkbox-circle-fill text-green-600 ri-lg" />
            ) : (
              <button className="cms-button append-button w-fit " style={{ fontSize: 15, padding: '8px 12px' }} onClick={handleValidateVatNo}>
                {validVatNo === false && !isValidating && <i className="ri-error-warning-line text-red-600 mr-1" />}
                {isValidating && <i className="ri-loader-4-line animate-spin mr-1" />}
                {isValidating ? <EventLabel name={'Validating'} groupName={'CMS.Payment'} /> : <EventLabel name={'Validate'} groupName={'CMS.Payment'} />}
              </button>
            )
          }
        />
      )}

      {((selectedPaymentType && selectedPaymentMethod === PaymentMethod.Invoice && selectedCountry) ||
        (selectedPaymentMethod !== PaymentMethod.Invoice &&
          selectedPaymentType === PaymentType.Comgov &&
          collectInvoiceInfoOnCompanyCardPayment &&
          selectedPaymentMethod &&
          selectedCountry)) && (
        <div className="mb-4">
          <div className="invoice-fields">
            <InvoiceFieldsNew
              {...{
                ...props,
                regionGroup,
                currentType: selectedPaymentType,
                selectedCountry,
                paymentMethods,
                selectedOption:
                  selectedPaymentMethod !== PaymentMethod.Invoice &&
                  selectedPaymentType === PaymentType.Comgov &&
                  collectInvoiceInfoOnCompanyCardPayment &&
                  selectedCountry
                    ? PaymentMethod.Invoice
                    : selectedPaymentMethod,
                setSelectedOption: setSelectedPaymentMethod,
                invoiceAnchor,
                mainEvent,
                validatePaymentFields,
                customValidations:
                  selectedPaymentMethod !== PaymentMethod.Invoice &&
                  selectedPaymentType === PaymentType.Comgov &&
                  collectInvoiceInfoOnCompanyCardPayment &&
                  selectedCountry
                    ? ['Name']
                    : null,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default observer(PaymentOptions);
